import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { LOCAL_STORAGE_KEYS } from '../utils/appConstant';
import { Permissions } from '../interfaces/common';
import { AuthContext } from '../contexts/AuthContext';
import { Theme } from '../interfaces/common';
import AuthService from '../services/auth.service';
import { API_ENDPOINTS } from '../utils/apiConstant';

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState<string>(() => AuthService.getLoggedInUser());
  const [token, setToken] = useState<string>(() => AuthService.getToken());
  const [isSystemAdmin, setIsSystemAdmin] = useState<string>(() => AuthService.getIsSystemAdmin());
  const [theme, setTheme] = useState<Theme>(() => AuthService.getTheme());
  const [permissions, setPermissions] = useState<Permissions>(() => AuthService.getPermissions());
  const [exception, setException] = useState<string>(() => AuthService.getException());
  const [loading, setLoading] = useState<string>(() => AuthService.getLoading());

  const logout = useCallback(async (url?: string) => {
    if(url !== API_ENDPOINTS.logout) {
      await AuthService.logout()
    }

    const tempLocalStorage: any = { ...LOCAL_STORAGE_KEYS };
    delete tempLocalStorage.theme;

    Object.keys(tempLocalStorage).forEach((item) => localStorage.removeItem(item));

    setLoggedInUser('{}')
    setToken('')
    setIsSystemAdmin('false')
    setPermissions({})
    setException('')
    setLoading('false')
  }, [setLoggedInUser, setToken, setIsSystemAdmin, setPermissions, setException, setLoading])

  const value = useMemo(() => {
    return {
      loggedInUser,
      token,
      isSystemAdmin,
      permissions,
      theme,
      exception,
      loading,
      logout,
      setLoggedInUser,
      setToken,
      setPermissions,
      setIsSystemAdmin,
      setTheme,
      setException,
      setLoading
    };
  }, [loggedInUser, permissions, token, isSystemAdmin, theme, exception, loading]);

  useEffect(() => {
    AuthService.storeLoggedInUser(loggedInUser);
    AuthService.storeToken(token);
    AuthService.storeIsSystemAdmin(isSystemAdmin);
    AuthService.storePermissions(permissions);
    AuthService.storeTheme(theme);
    AuthService.storeException(exception);
    AuthService.storeLoading(loading);
  }, [loggedInUser, isSystemAdmin, token, theme, permissions, exception, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
